<template>
  <div>
    <FormLabel v-if="label" :label="label" :error="!!error" />
    <div class="flex h-9 w-full items-center timepicker rounded-4 text-black text-13 leading-16 outline-none border border-solid p-2 bg-white">
      <input v-model="hour" ref="hour" maxlength="2" class="w-6 border-none outline-0 text-center" @input="onHourInput" @keydown="onKeydown" @focus="hourFocused = true" @blur="onHourBlur" />
      <span class="px-0.5">:</span>
      <input v-model="minute" ref="minute" maxlength="2" class="w-6 border-none outline-0 text-center" @input="onMinuteInput" @focus="minuteFocused = true" @blur="onMinuteBlur" />
      <Icon name="schedule" class="ml-auto w-4 flex-shrink-0 opacity-50" />
    </div>
    <label v-if="error || $slots['error']" class="text-12 text-alert mt-3 mb-1">{{ error }} <slot name="error"></slot></label>
  </div>
</template>

<script>
import ValidationMixin from '../mixins/ValidationMixin.js';
import FormLabel from './FormLabel.vue';

export default {
  name: 'FormTimeInput',
  components: { FormLabel },
  mixins: [ValidationMixin],
  emits: ['update:modelValue', 'focus', 'blur', 'keyup'],
  compatConfig: { COMPONENT_V_MODEL: false },
  props: {
    type: {
      type: String,
      default: 'text',
    },
    disabled: Boolean,
    readonly: Boolean,
    modelValue: {
      type: [String, Number],
      default: '',
    },
    label: String,
    error: String,
    min: String,
    max: String,
    id: String,
    icon: String,
    loading: Boolean,
    addNameAttr: {
      type: Boolean,
    },
    maxlength: {
      type: String,
    },
    placeholder: {
      type: String,
    },
  },
  data() {
    return {
      hour: '00',
      minute: '00',
      hourFocused: false,
      minuteFocused: false,
    };
  },
  methods: {
    onHourInput(e) {
      if (e.target.value === '') {
        this.hour = 0;
      } else if (parseInt(e.target.value, 10) > 23) {
        this.hour = 0;
      } else if (parseInt(e.target.value, 10) < 0) {
        this.hour = 0;
      }

      if (this.$refs.hour.selectionStart === 2) {
        this.$refs.minute.focus();
        this.$refs.minute.setSelectionRange(0, 2);
      }
      this.updateValue();
    },
    onMinuteInput(e) {
      if (e.target.value === '') {
        this.minute = 0;
      } else if (parseInt(e.target.value, 10) > 59) {
        this.minute = 0;
      } else if (parseInt(e.target.value, 10) < 0) {
        this.minute = 0;
      }
      this.updateValue();
    },
    focus() {
      this.$refs.input.$el.focus();
    },
    updateValue() {
      const value = `${this.hour}:${this.minute}`;
      this.eventbus.emit('input', value);
      this.$emit('update:modelValue', value);
    },
    onHourBlur() {
      this.hour = `0${this.hour}`.slice(-2);
      this.hourFocused = false;
    },
    onMinuteBlur() {
      this.minute = `0${this.minute}`.slice(-2);
      this.minuteFocused = false;
    },
    onKeydown(e) {
      if (
        [46, 8, 9, 27, 13].includes(e.keyCode) ||
        // Allow: Ctrl+A, Command+A
        (e.keyCode === 65 && (e.ctrlKey === true || e.metaKey === true)) ||
        // Allow: home, end, left, right, down, up
        (e.keyCode >= 35 && e.keyCode <= 40)
      ) {
        // let it happen, don't do anything
        return;
      }
      // Ensure that it is a number and stop the keypress
      if ((e.shiftKey || e.keyCode < 48 || e.keyCode > 57) && (e.keyCode < 96 || e.keyCode > 105)) {
        e.preventDefault();
      }
    },
  },
  watch: {
    modelValue: {
      handler() {
        if (!this.modelValue?.includes('undefined')) {
          const hour = this.modelValue.split(':')[0];
          const minute = this.modelValue.split(':')[1];
          this.minute = `${minute}`.slice(-2);
          this.hour = `${hour}`.slice(-2);

          if (!this.minuteFocused) {
            this.minute = `0${this.minute}`.slice(-2);
          }
          if (!this.hourFocused) {
            this.hour = `0${this.hour}`.slice(-2);
          }
        }
      },
      immediate: true,
    },
  },
};
</script>
<style>
.timepicker input::-webkit-outer-spin-button,
.timepicker input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

/* Firefox */
.timepicker input[type='number'] {
  -moz-appearance: textfield;
}
</style>
